import { useEffect, useRef, useCallback } from "react";

interface UseScrollBoundaryProps {
    scrollAreaRef: React.RefObject<HTMLElement>;
    direction?: "horizontal" | "vertical"; // default horizontal
    onBoundaryChange?: (position: "start" | "end" | null) => void;
    horizontalScrollThreshold?: number; // margin from edges (default 5px)
    verticalScrollThreshold?: number; // margin from edges (default 5px)
    activationDistance?: number; // min scroll movement before activation (default 30px)
    onVerticalScroll?: (scroll: 'up' | 'down') => void;
    /**
     * Time in ms to consider a sequence of wheel events a single gesture. During
     * a gesture we will emit only one up/down event. Default: 200ms
     */
    verticalGestureTimeout?: number;
}

/**
 * A hook that listens for scroll events in a given scroll area,
 * and checks if the user has scrolled beyond a certain activation distance,
 * and if so, fires an onBoundaryChange callback with the position
 * (either "start", "end", or null).
 *
 * @param {Object} props - an object containing the following properties:
 * @param {React.RefObject<HTMLElement>} scrollAreaRef - a reference to the scroll area element
 * @param {"horizontal" | "vertical"} [direction="horizontal"] - the direction of the scroll area (default horizontal)
 * @param {function(position: "start" | "end" | null)} [onBoundaryChange] - an optional callback to fire when the boundary is changed
 * @param {function(scroll: "up" | "down")} [onVerticalScroll] - an optional callback to fire when the user scrolls vertically
 * @param {number} [horizontalScrollThreshold=5] - an optional horizontalScrollThreshold value (in px) from the scroll area's edges
 * @param {number} [verticalScrollThreshold=20] - an optional verticalScrollThreshold value (in px) from the scroll area's edges
 * @param {number} [activationDistance=30] - an optional activation distance (in px) from the scroll area's edges
 */
export const useScrollBoundary = ({
    scrollAreaRef,
    direction = "horizontal",
    onBoundaryChange,
    onVerticalScroll,
    horizontalScrollThreshold = 5,
    verticalScrollThreshold = 5,
    activationDistance = 30,
    verticalGestureTimeout = 200,
}: UseScrollBoundaryProps) => {
    const isActive = useRef(false);
    const startScrollPos = useRef(0);
    const lastPosition = useRef<"start" | "end" | null>(null);
    const verticalGestureActive = useRef(false);
    const verticalGestureTimeoutRef = useRef<number | null>(null);

    const handleScroll = useCallback(() => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea) return;

        const currentPos = direction === "horizontal" ? scrollArea.scrollLeft : scrollArea.scrollTop;
        const delta = Math.abs(currentPos - startScrollPos.current);

        // user must scroll beyond activationDistance to start detecting
        if (!isActive.current && delta > activationDistance) {
            isActive.current = true;
        }

        // still inactive → ignore
        if (!isActive.current) return;

        let position: "start" | "end" | null = null;

        if (direction === "horizontal") {
            const { scrollLeft, scrollWidth, clientWidth } = scrollArea;
            if (scrollLeft <= horizontalScrollThreshold) position = "start";
            else if (scrollLeft + clientWidth >= scrollWidth - horizontalScrollThreshold) position = "end";
        } else {
            const { scrollTop, scrollHeight, clientHeight } = scrollArea;
            if (scrollTop <= horizontalScrollThreshold) position = "start";
            else if (scrollTop + clientHeight >= scrollHeight - horizontalScrollThreshold) position = "end";
        }

        // only fire if boundary changed
        if (position !== lastPosition.current) {
            lastPosition.current = position;
            onBoundaryChange?.(position);
        }
    }, [scrollAreaRef, direction, horizontalScrollThreshold, onBoundaryChange, activationDistance]);

    const handleWheel = useCallback((e: WheelEvent) => {
        if (Math.abs(e.deltaY) < verticalScrollThreshold) return;
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            if (onVerticalScroll) {
                e.preventDefault();
                const dir: 'up' | 'down' = e.deltaY > 0 ? 'down' : 'up';
                if (!verticalGestureActive.current) {
                    verticalGestureActive.current = true;
                    onVerticalScroll(dir);
                }

                if (verticalGestureTimeoutRef.current) {
                    window.clearTimeout(verticalGestureTimeoutRef.current);
                }
                verticalGestureTimeoutRef.current = window.setTimeout(() => {
                    verticalGestureActive.current = false;
                    verticalGestureTimeoutRef.current = null;
                }, verticalGestureTimeout);
            }
        }
    }, [verticalScrollThreshold, onVerticalScroll, verticalGestureTimeout]);



    useEffect(() => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea) return;

        // initial scroll position store
        startScrollPos.current = direction === "horizontal" ? scrollArea.scrollLeft : scrollArea.scrollTop;

        scrollArea.addEventListener("scroll", handleScroll, { passive: true });
        scrollArea.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            scrollArea.removeEventListener("scroll", handleScroll);
            scrollArea.removeEventListener("wheel", handleWheel);
            if (verticalGestureTimeoutRef.current) {
                window.clearTimeout(verticalGestureTimeoutRef.current);
                verticalGestureTimeoutRef.current = null;
            }
        };
    }, [scrollAreaRef, direction, handleScroll, handleWheel]);
};
